import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import _ from 'lodash';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { KnittingUtil } from '@app/utilities/knitting-util';
import { Asset } from '../models/asset/asset';
import { Greige } from '../models/greige/greige';
import { Roll } from '../models/knitting/roll';
import { Fabric } from '../models/order/work-order';
import { KnittingOrder } from '../models/production/knitting-order';
import { RollProxyService } from '../proxies/production/roll.proxy.service';
import { RollLabelProxyService } from '../proxies/production/roll-label.proxy.service';

export interface KnittingMachineRolls {
  asset: Asset;
  fabric: Fabric;
  variantCode: string;
  savedRolls: Roll[];
  isActive: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class KnittingMachineRollsService {
  /**
   * We now track combos by "assetId|fabricCode" instead of just assetId.
   */
  private machineRollsMap = new Map<string, KnittingMachineRolls>();
  public STANDARD_ROLL_WEIGHT = 60; // Standard roll weight in kg

  private allAssets: Asset[] = [];
  private allFabrics: Fabric[] = [];
  public availableAssets: Asset[] = [];
  public knittingOrder: KnittingOrder | null = null;

  public selectedAsset: Asset | null = null;
  public selectedGreige: Greige | null = null;
  public editingComboKey: string | null = null;

  constructor(
    private rollProxyService: RollProxyService,
    private rollPrintProxyService: RollLabelProxyService,
  ) {}

  // --------------------------------------
  //           PUBLIC METHODS
  // --------------------------------------
  public resetServiceState(): void {
    this.machineRollsMap.clear();
    this.allAssets = [];
    this.allFabrics = [];
    this.availableAssets = [];
    this.knittingOrder = null;
    this.selectedAsset = null;
    this.selectedGreige = null;
    this.editingComboKey = null;
  }

  /**
   * Initialize the service with the loaded data:
   * - allAssets (for picking combos)
   * - knittingOrder
   * - existing rolls
   * - Greiges
   */
  public initializeData(assets: Asset[], fabrics: Fabric[], rolls: Roll[], knittingOrder: KnittingOrder): void {
    this.allAssets = assets;
    this.knittingOrder = knittingOrder;
    this.allFabrics = fabrics;

    // For each existing roll, we see which asset + greige it belongs to
    rolls.forEach((roll) => {
      const asset = this.allAssets.find((a) => a.assetId === roll.assetId);

      const fabric = fabrics.find((f) => f.fabricCode === roll.expectedFabricCode);

      if (asset && fabric) {
        // create or update the combination record
        const comboKey = KnittingUtil.getComboKey(asset.assetId, fabric.fabricCode, roll.knittingOrderVariantCode || '');
        if (!this.machineRollsMap.has(comboKey)) {
          this.machineRollsMap.set(comboKey, {
            asset,
            fabric: fabric,
            variantCode: roll.knittingOrderVariantCode || '',
            savedRolls: [],
            isActive: true,
          });
        }
        // now push the saved roll
        this.machineRollsMap.get(comboKey)?.savedRolls.push(roll);
      }
    });

    // assets not used in rolls => remain in "availableAssets"
    // or you can store them if you want the user to add combos later
    this.availableAssets = this.allAssets.filter((a) => {
      // check if there is any combo with this asset
      for (const key of this.machineRollsMap.keys()) {
        if (key.startsWith(a.assetId + '|')) return false;
      }
      return true;
    });
  }

  //#region Proxy Actions

  public batchCreateRolls(
    assets: Asset[],
    variantCode: string,
    fabricCode: string,
    quantityToBeKnitted: number,
    itemCode: string,
  ): Observable<Roll[]> {
    const knittingOrder = this.knittingOrder;
    if (quantityToBeKnitted <= 0) {
      GlobalMessageUtil.warn('Number of rolls must be greater than 0', 'Please enter a valid number of rolls');
      return new Observable<Roll[]>((observer) => {
        observer.next([]);
        observer.complete();
      });
    }

    const fabric = this.allFabrics.find((f) => f.fabricCode === fabricCode);

    if (_.isNil(fabric) || _.isNil(fabric.greige) || _.isNil(fabric.greige.greigeCode)) {
      GlobalMessageUtil.error('Fabric greige code is missing', 'Please select a valid fabric');
      return new Observable<Roll[]>((observer) => {
        observer.next([]);
        observer.complete();
      });
    }

    if (_.isNil(variantCode) || _.isEmpty(variantCode)) {
      console.warn('Variant code is missing');
      GlobalMessageUtil.warn('Variant code is missing', 'Please select a valid variant code');
      return new Observable<Roll[]>((observer) => {
        observer.next([]);
        observer.complete();
      });
    }

    if (_.isNil(knittingOrder?.knittingOrderNumber) || _.isNil(knittingOrder?.id)) {
      console.warn('Knitting order number is missing');
      GlobalMessageUtil.error('Knitting order number is missing', 'Please select a valid knitting order or contact support');
      return new Observable<Roll[]>((observer) => {
        observer.next([]);
        observer.complete();
      });
    }
    const totalRollsNeeded = Math.ceil(quantityToBeKnitted / this.STANDARD_ROLL_WEIGHT);
    const approximateNumberOfRollsPerAsset = Math.ceil(totalRollsNeeded / assets.length);

    let remainingQty = quantityToBeKnitted;
    const rollsToCreate: Roll[] = [];

    _.forEach(assets, (asset) => {
      let rollsAssigned = 0;

      while (remainingQty > 0 && rollsAssigned < approximateNumberOfRollsPerAsset && rollsToCreate.length < totalRollsNeeded) {
        const weight = Math.min(remainingQty, this.STANDARD_ROLL_WEIGHT);

        rollsToCreate.push({
          itemCode: itemCode,
          status: 0,
          rollQuality: 0,
          knittingOrderVariantCode: variantCode,
          knittingOrderNumber: knittingOrder.knittingOrderNumber!,
          knittingOrderReference: knittingOrder.id!,
          expectedFabricCode: fabric.fabricCode,
          originalFabricCode: fabric.fabricCode,
          assetId: asset.assetId,
          plannedWeight: weight,
          stationTimeStamps: [],
          isActive: true,
        });

        remainingQty -= weight;
        rollsAssigned++;
      }
    });

    if (rollsToCreate.length === 0) {
      console.warn('No rolls to create');
      return new Observable<Roll[]>((observer) => {
        observer.next([]);
        observer.complete();
      });
    }

    return new Observable<Roll[]>((observer) => {
      this.rollProxyService.createRollBatch(rollsToCreate).subscribe({
        next: (result) => {
          if (!_.isNil(result.data)) {
            // Update the machineRollsMap with the new rolls
            result.data.forEach((roll) => {
              const asset = this.allAssets.find((a) => a.assetId === roll.assetId);
              const fabric = this.allFabrics.find((f) => f.fabricCode === roll.expectedFabricCode);

              if (asset && fabric) {
                const comboKey = KnittingUtil.getComboKey(asset.assetId, fabric.fabricCode, roll.knittingOrderVariantCode || '');
                if (!this.machineRollsMap.has(comboKey)) {
                  this.machineRollsMap.set(comboKey, {
                    asset,
                    fabric: fabric,
                    variantCode: roll.knittingOrderVariantCode || '',
                    savedRolls: [],
                    isActive: true,
                  });
                }
                this.machineRollsMap.get(comboKey)?.savedRolls.push(roll);
              }
            });
            GlobalMessageUtil.success('Rolls created successfully', `${result.data.length} roll(s) have been created successfully.`);
            observer.next(result.data!);
            observer.complete();
            return;
          }
          GlobalMessageUtil.error('Error creating rolls', 'No data returned from the server. Please try again later.');
          observer.next([]);
          observer.complete();
        },
        error: (err) => {
          console.error('Error creating rolls:', err);
          observer.next([]);
          observer.complete();
        },
      });
    });
  }

  public deleteRoll(rollId: string): void {
    //find roll in all combos
    const roll = Array.from(this.machineRollsMap.values())
      .flatMap((combo) => combo.savedRolls)
      .find((r) => r.id === rollId);
    if (!roll) {
      console.warn('Roll not found in any combo:', rollId);
      GlobalMessageUtil.error('Roll not found', 'The roll you are trying to delete does not exist or has already been deleted.');
      return;
    }

    if (!_.isEmpty(roll.stationTimeStamps)) {
      console.warn('Roll has station time stamps and cannot be deleted:', rollId);
      GlobalMessageUtil.error('Cannot delete roll', 'The roll has station time stamps and cannot be deleted.');
      return;
    }
    this.rollProxyService.deleteRoll(rollId).subscribe({
      next: () => {
        // Remove the roll from the combo
        for (const [key, combo] of this.machineRollsMap.entries()) {
          const rollIndex = combo.savedRolls.findIndex((r) => r.id === rollId);
          if (rollIndex !== -1) {
            combo.savedRolls.splice(rollIndex, 1);
            // If no rolls left, mark the combo as inactive
            if (combo.savedRolls.length === 0) {
              combo.isActive = false;
            }
            this.machineRollsMap.set(key, combo);
            break; // Exit loop once we find and remove the roll
          }
        }
        GlobalMessageUtil.success('Roll deleted successfully', 'The roll has been deleted successfully.');
      },
      error: (err) => {
        console.error('Error deleting roll:', err);
      },
    });
  }

  //#endregion

  //#region Getters
  public getAllCombos(): KnittingMachineRolls[] {
    // Return all active combos
    return Array.from(this.machineRollsMap.values()).filter((combo) => combo.isActive);
  }

  public getSelectedCombo(): KnittingMachineRolls | undefined {
    if (_.isEmpty(this.editingComboKey) || _.isNil(this.editingComboKey)) return undefined;
    const record = this.machineRollsMap.get(this.editingComboKey);
    if (record?.isActive === false) {
      this.editingComboKey = null;
      return undefined;
    }
    return record;
  }

  public getCombo(assetId: string, fabricCode: string, variantCode: string): KnittingMachineRolls | undefined {
    return this.machineRollsMap.get(KnittingUtil.getComboKey(assetId, fabricCode, variantCode));
  }

  /**
   * Print all saved rolls for the currently selected combo
   */
  public printSavedRolls(rollNumbers: string[], comboKey: string): Observable<boolean> {
    if (_.isNil(comboKey)) {
      console.warn('No combo selected');
      return new Observable<boolean>((observer) => observer.next(false));
    }
    const record = this.machineRollsMap.get(comboKey);
    if (!record) {
      console.warn('No record found for combo key:', this.editingComboKey);
      return new Observable<boolean>((observer) => observer.next(false));
    }
    const checkRollNumbers = record.savedRolls.filter((r) => rollNumbers.includes(r.number!));
    if (checkRollNumbers.length !== rollNumbers.length) {
      console.warn('Some roll numbers not found in saved rolls');
      return new Observable<boolean>((observer) => observer.next(false));
    }

    if (!rollNumbers.length) {
      console.warn('No saved rolls to print');
      return new Observable<boolean>((observer) => observer.next(false));
    }

    return new Observable<boolean>((observer) => {
      this.rollPrintProxyService.getPlanningLabelsByRollNumbers(rollNumbers).subscribe({
        next: (blob) => {
          //dowload as pdf file
          if (!(blob instanceof Blob)) {
            console.error('Expected a Blob but received:', blob);
            observer.next(false);
            observer.complete();
            return;
          }

          //update rolls latestPrintedDate

          const currentDate = new Date();
          checkRollNumbers.forEach((roll) => {
            roll.lastPrintedDate = currentDate;
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `qr-codes.pdf`;
          link.click();

          // Cleanup
          URL.revokeObjectURL(url);
          observer.next(true);
          observer.complete();
        },
        error: (err) => {
          console.error('Error printing rolls', err);
          observer.next(false);
          observer.complete();
        },
      });
    });
  }

  //* this method print multiple rolls in only file
  public printRollsFromMultipleCombos(rollNumbers: string[], selectedComboKeys: Set<string>): Observable<boolean> {
    if (!rollNumbers.length) {
      console.warn('No roll numbers provided');
      return new Observable<boolean>((observer) => {
        observer.next(false);
        observer.complete();
      });
    }

    if (!selectedComboKeys.size) {
      console.warn('No combo keys provided');
      return new Observable<boolean>((observer) => {
        observer.next(false);
        observer.complete();
      });
    }

    // Collect all rolls from all selected combos
    const allRollsFromCombos: Roll[] = [];
    const rollsToUpdate: Roll[] = [];

    selectedComboKeys.forEach((comboKey) => {
      const record = this.machineRollsMap.get(comboKey);
      if (record) {
        allRollsFromCombos.push(...record.savedRolls);
      }
    });

    // Find the rolls that match the selected roll numbers
    rollNumbers.forEach((rollNumber) => {
      const foundRoll = allRollsFromCombos.find((roll) => roll.number === rollNumber);
      if (foundRoll) {
        rollsToUpdate.push(foundRoll);
      }
    });

    // Validate that all requested rolls were found
    if (rollsToUpdate.length !== rollNumbers.length) {
      const foundNumbers = rollsToUpdate.map((r) => r.number);
      const missingNumbers = rollNumbers.filter((num) => !foundNumbers.includes(num));
      console.warn('Some roll numbers not found across selected combos:', missingNumbers);
      GlobalMessageUtil.warn('Some rolls not found', `Could not find rolls: ${missingNumbers.join(', ')} in the selected combos`);

      return new Observable<boolean>((observer) => {
        observer.next(false);
        observer.complete();
      });
    }

    // Now proceed with printing
    return new Observable<boolean>((observer) => {
      this.rollPrintProxyService.getPlanningLabelsByRollNumbers(rollNumbers).subscribe({
        next: (blob) => {
          if (!(blob instanceof Blob)) {
            console.error('Expected a Blob but received:', blob);
            observer.next(false);
            observer.complete();
            return;
          }

          // Update all found rolls with current print date
          const currentDate = new Date();
          rollsToUpdate.forEach((roll) => {
            roll.lastPrintedDate = currentDate;
          });

          // Download the PDF
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `qr-codes-${new Date().toISOString().split('T')[0]}.pdf`;
          link.click();

          // Cleanup
          URL.revokeObjectURL(url);
          observer.next(true);
          observer.complete();
        },
        error: (err) => {
          console.error('Error printing rolls from multiple combos:', err);
          observer.next(false);
          observer.complete();
        },
      });
    });
  }

  /**
   * Return all combos
   */
  public getAllKnittingMachineRolls(): KnittingMachineRolls[] {
    //get all active combos
    return Array.from(this.machineRollsMap.values()).filter((r) => r.isActive);
  }

  getRollsByComboKey(comboKey: string): Roll[] {
    const record = this.machineRollsMap.get(comboKey);
    if (!record) {
      console.warn('No record found for combo key:', comboKey);
      return [];
    }
    return record.savedRolls;
  }

  //#region Helper Methods
  public getComboKey(assetId: string, fabricCode: string): string {
    return `${assetId}|${fabricCode}`;
  }
  public getNextVariantCode(fabricCode: string, controls: FormGroup[]): string {
    const matchingDetails = _.filter(controls, (formGroup) => formGroup.value.fabric?.fabricCode === fabricCode);

    const highestNumber = matchingDetails.reduce((max, formGroup) => {
      const code: string = formGroup.value.variantCode || '';
      const match = code.match(/Entry (\d+)/);
      if (match) {
        const num = parseInt(match[1], 10);
        return Math.max(max, num);
      }
      return max;
    }, 0);

    return `Entry ${highestNumber + 1}`;
  }

  public deleteRollsByBatch(rollNumbers: string[]): Observable<string[]> {
    return new Observable<string[]>((subscriber) => {
      const existingRolls = this.validateRollsExist(rollNumbers);

      if (existingRolls.length !== rollNumbers.length) {
        const missing = rollNumbers.filter((r) => !existingRolls.includes(r));

        GlobalMessageUtil.warn('Some rolls not found', `Couldn't find rolls: ${missing.join(', ')}`);
        subscriber.error('Some rolls not found');

        return;
      }

      this.rollProxyService.deleteRollsByBatch(rollNumbers).subscribe({
        next: (result) => {
          if (result.success) {
            // Update local state
            this.filterDeletedRolls(rollNumbers);

            subscriber.next(rollNumbers);
            subscriber.complete();

            GlobalMessageUtil.success('Success', `Deleted ${rollNumbers.length} rolls successfully`);
          } else {
            subscriber.error('Deletion failed');
          }
        },
        error: (err) => {
          console.error('Batch deletion error:', err);
          subscriber.error(err);
        },
      });
    });
  }

  //* this is for to validate that rolls exist in current data
  private validateRollsExist(rollNumbers: string[]): string[] {
    const allRolls = Array.from(this.machineRollsMap.values())
      .flatMap((combo) => combo.savedRolls)
      .map((roll) => roll.number!);

    return rollNumbers.filter((num) => allRolls.includes(num));
  }

  //* this is for to remove deleted rolls from local state
  private filterDeletedRolls(rollNumbers: string[]): void {
    const updatedMap = new Map(this.machineRollsMap);

    updatedMap.forEach((combo, key) => {
      const originalCount = combo.savedRolls.length;
      combo.savedRolls = combo.savedRolls.filter((roll) => !rollNumbers.includes(roll.number!));

      if (combo.savedRolls.length !== originalCount) {
        combo.isActive = combo.savedRolls.length > 0;
        updatedMap.set(key, combo);
      }
    });

    this.machineRollsMap = updatedMap;
  }

  //#endregion

  public clearData(): void {
    this.machineRollsMap.clear();
    this.availableAssets = [];
    this.allAssets = [];
    this.knittingOrder = null;
    this.selectedAsset = null;
    this.selectedGreige = null;
    this.editingComboKey = null;
  }
}
